import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

import schoolLogo from "./zpa.png";
import studentImage from "./zpagirl.png";

const COLORS = {
  BLACK: "#111827",
  DARK: "#1F2937",
  GOLD: "#C8A74E",
  GOLD_DARK: "#A67C1A",
  GOLD_SOFT: "#FAF6E8",
  ORANGE: "#F97316",
  WHITE: "#FFFFFF",
  LIGHT: "#F8FAFC",
  LIGHT_ALT: "#F1F5F9",
  BORDER: "#E2E8F0",
  TEXT: "#374151",
  MUTED: "#64748B",
  RED_SOFT: "#FFF1F2",
  ORANGE_SOFT: "#FFF7ED",
};

const FONT = {
  TITLE: 28,
  SUBTITLE: 16,
  HEADER: 13,
  SECTION: 10,
  BODY: 9,
  SMALL: 8,
  TINY: 7,
};

const styles = StyleSheet.create({

  page: {
    backgroundColor: COLORS.WHITE,
    padding: 18,
    fontFamily: "Helvetica",
    color: COLORS.TEXT,
    fontSize: FONT.BODY,
  },

  spread: {
    flexDirection: "row",
    flex: 1,
    width: "100%",
  },

  panelLeft: {
    width: "50%",
    paddingRight: 8,
  },

  panelRight: {
    width: "50%",
    paddingLeft: 8,
  },

  coverPanel: {
    flex: 1,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    borderRadius: 6,
    padding: 22,
    position: "relative",
    overflow: "hidden",
    backgroundColor: COLORS.WHITE,
  },

  goldLine: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 5,
    backgroundColor: COLORS.GOLD,
  },

  darkLine: {
    position: "absolute",
    top: 8,
    left: 0,
    width: "45%",
    height: 3,
    backgroundColor: COLORS.BLACK,
  },

  coverTop: {
    flexDirection: "row",
    alignItems: "center",
  },

  coverLogo: {
    width: 75,
    height: 75,
    marginRight: 15,
  },

  coverBrand: {
    flex: 1,
  },

  coverSchoolName: {
    fontSize: 18,
    fontWeight: "bold",
    color: COLORS.BLACK,
    lineHeight: 1.2,
  },

  coverMotto: {
    marginTop: 5,
    fontSize: FONT.SMALL,
    color: COLORS.GOLD_DARK,
    fontWeight: "bold",
  },

  coverMain: {
    marginTop: 45,
    width: "65%",
    zIndex: 2,
  },

  title: {
    fontSize: FONT.TITLE,
    fontWeight: "bold",
    color: COLORS.BLACK,
    lineHeight: 1.15,
  },

  subtitle: {
    marginTop: 8,
    fontSize: FONT.SUBTITLE,
    color: COLORS.ORANGE,
    fontWeight: "bold",
  },

  yearBox: {
    marginTop: 18,
    backgroundColor: COLORS.BLACK,
    paddingVertical: 8,
    paddingHorizontal: 18,
    borderRadius: 5,
    alignSelf: "flex-start",
  },

  yearText: {
    color: COLORS.WHITE,
    fontWeight: "bold",
    fontSize: 12,
  },

  coverText: {
    marginTop: 18,
    fontSize: FONT.BODY,
    color: COLORS.MUTED,
    lineHeight: 1.6,
    textAlign: "justify",
  },

  studentImage: {
    position: "absolute",
    right: -20,
    bottom: -15,
    width: 230,
    height: 290,
    opacity: 0.92,
  },

  imageFade: {
    position: "absolute",
    right: 0,
    bottom: 0,
    width: 220,
    height: 80,
    backgroundColor: COLORS.WHITE,
    opacity: 0.35,
  },

  badge: {
    position: "absolute",
    right: 25,
    top: 30,
    width: 70,
    height: 70,
    borderRadius: 40,
    backgroundColor: COLORS.GOLD_SOFT,
    borderWidth: 1,
    borderColor: COLORS.GOLD,
    justifyContent: "center",
    alignItems: "center",
  },

  badgeText: {
    fontSize: 7,
    textAlign: "center",
    color: COLORS.BLACK,
    fontWeight: "bold",
  },

  schoolHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.GOLD,
  },

  logo: {
    width: 55,
    height: 55,
    marginRight: 10,
  },

  schoolName: {
    fontSize: 15,
    fontWeight: "bold",
    color: COLORS.BLACK,
  },

  motto: {
    marginTop: 3,
    fontSize: FONT.SMALL,
    color: COLORS.GOLD_DARK,
    fontWeight: "bold",
  },

  contact: {
    marginTop: 4,
    fontSize: FONT.TINY,
    color: COLORS.MUTED,
    lineHeight: 1.4,
  },

  card: {
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    borderRadius: 6,
    padding: 9,
    marginBottom: 8,
    backgroundColor: COLORS.WHITE,
  },

  goldCard: {
    borderWidth: 1,
    borderColor: COLORS.GOLD,
    borderRadius: 6,
    padding: 9,
    marginBottom: 8,
    backgroundColor: COLORS.GOLD_SOFT,
  },

  softCard: {
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    borderRadius: 6,
    padding: 9,
    marginBottom: 8,
    backgroundColor: COLORS.LIGHT,
  },

  dangerCard: {
    borderWidth: 1,
    borderColor: "#FED7AA",
    borderRadius: 6,
    padding: 9,
    marginBottom: 8,
    backgroundColor: COLORS.ORANGE_SOFT,
  },

  sectionTitle: {
    backgroundColor: COLORS.BLACK,
    color: COLORS.WHITE,
    fontSize: FONT.SECTION,
    fontWeight: "bold",
    paddingVertical: 5,
    paddingHorizontal: 7,
    borderRadius: 3,
    marginBottom: 7,
  },

  heading: {
    fontSize: FONT.HEADER,
    fontWeight: "bold",
    color: COLORS.BLACK,
    marginBottom: 5,
  },

  body: {
    fontSize: FONT.BODY,
    color: COLORS.TEXT,
    lineHeight: 1.5,
    textAlign: "justify",
  },

  bulletRow: {
    flexDirection: "row",
    marginBottom: 4,
  },

  bullet: {
    width: 10,
    fontSize: FONT.BODY,
    color: COLORS.GOLD_DARK,
    fontWeight: "bold",
  },

  bulletText: {
    flex: 1,
    fontSize: FONT.BODY,
    color: COLORS.TEXT,
    lineHeight: 1.4,
  },

  divider: {
    height: 1,
    backgroundColor: COLORS.BORDER,
    marginVertical: 8,
  },

  footer: {
    position: "absolute",
    bottom: 10,
    left: 18,
    right: 18,
    borderTopWidth: 1,
    borderTopColor: COLORS.BORDER,
    paddingTop: 5,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },

  footerLogo: {
    width: 18,
    height: 18,
    marginRight: 5,
  },

  footerText: {
    fontSize: FONT.TINY,
    color: COLORS.MUTED,
  },

});

export const Divider = () => (
  <View style={styles.divider} />
);

export const Bullet = ({ children }) => (
  <View style={styles.bulletRow}>
    <Text style={styles.bullet}>•</Text>
    <Text style={styles.bulletText}>
      {children}
    </Text>
  </View>
);

export const Section = ({
  title,
  children,
  variant = "normal",
}) => {

  const container =
    variant === "gold"
      ? styles.goldCard
      : variant === "soft"
      ? styles.softCard
      : variant === "danger"
      ? styles.dangerCard
      : styles.card;

  return (
    <View style={container}>
      <Text style={styles.sectionTitle}>
        {title}
      </Text>

      {children}

    </View>
  );
};


export const SchoolHeader = ({
  school = {},
}) => (

  <View style={styles.schoolHeader}>

    <Image
      src={school.logo || schoolLogo}
      style={styles.logo}
    />

    <View
      style={{
        flex: 1,
      }}
    >

      <Text style={styles.schoolName}>
        {
          school.name ||
          "ZION PRAISE ACADEMY"
        }
      </Text>

      <Text style={styles.motto}>
        {
          school.motto ||
          "Learning To Make A Difference"
        }
      </Text>

      <Text style={styles.contact}>
        {
          school.address ||
          "Monrovia, Liberia"
        }

        {"\n"}

        {
          school.phone ||
          "+231 XXX XXX XXX"
        }

        {"\n"}

        {
          school.email ||
          "info@zionpraiseacademy.com"
        }

      </Text>

    </View>

  </View>

);


export const Footer = ({
  school = {},
}) => (

  <View style={styles.footer}>

    <Image
      src={
        school.logo ||
        schoolLogo
      }
      style={styles.footerLogo}
    />

    <Text style={styles.footerText}>

      {
        school.name ||
        "Zion Praise Academy"
      }

      {" • "}

      {
        school.website ||
        "www.zionpraiseacademy.com"
      }

    </Text>

  </View>

);



const CoverOutside = ({
  academicYear,
  school = {},
}) => (

<Page
  size="A4"
  orientation="landscape"
  style={styles.page}
>

<View style={styles.spread}>


<View style={styles.panelLeft}>

<View style={styles.coverPanel}>


<View style={styles.goldLine}/>

<View style={styles.darkLine}/>



<View style={styles.coverTop}>

<Image
  src={
    school.logo ||
    schoolLogo
  }
  style={styles.coverLogo}
/>


<View style={styles.coverBrand}>

<Text style={styles.coverSchoolName}>

{
school.name ||
"ZION PRAISE ACADEMY"
}

</Text>


<Text style={styles.coverMotto}>

{
school.motto ||
"Learning To Make A Difference"
}

</Text>


</View>


</View>



<View style={styles.coverMain}>


<Text style={styles.title}>

SCHOOL

</Text>


<Text style={styles.title}>

INFORMATION

</Text>


<Text style={styles.title}>

HANDBOOK

</Text>



<Text style={styles.subtitle}>

ACADEMIC YEAR GUIDE

</Text>



<View style={styles.yearBox}>

<Text style={styles.yearText}>

{
academicYear?.name ||
"2026 / 2027"
}

</Text>

</View>



<Text style={styles.coverText}>

This handbook provides important school
information including academic policies,
admission requirements, school fees,
uniform guidelines, student expectations
and parent responsibilities.

</Text>


</View>



<Image
src={studentImage}
style={styles.studentImage}
/>


<View style={styles.imageFade}/>




</View>

</View>





<View style={styles.panelRight}>


<View style={styles.coverPanel}>


<View style={styles.goldLine}/>



<SchoolHeader
school={school}
/>



<Section
title="WELCOME"
variant="gold"
>


<Text style={styles.body}>

Welcome to{" "}

{
school.name ||
"Zion Praise Academy"
}.

{"\n\n"}

We are committed to providing a safe,
disciplined and inspiring learning
environment where every learner can
achieve academic excellence while
developing strong moral values,
leadership skills and confidence.

</Text>


</Section>





<Section
title="WHY CHOOSE US"
>


<Bullet>
Qualified and experienced teachers.
</Bullet>


<Bullet>
Modern learning environment.
</Bullet>


<Bullet>
Strong academic foundation.
</Bullet>


<Bullet>
Character and leadership development.
</Bullet>


<Bullet>
Affordable quality education.
</Bullet>


</Section>





<Section
title="OUR MOTTO"
variant="soft"
>


<Text
style={{
textAlign:"center",
fontSize:13,
fontWeight:"bold",
color:COLORS.GOLD_DARK
}}
>


"
{
school.motto ||
"Learning To Make A Difference"
}
"


</Text>


</Section>





<Section
title="OUR VISION"
variant="gold"
>


<Text style={styles.body}>

To see students as Christ centered leaders commited to positively influrence their generations.

</Text>


</Section>



<Footer
school={school}
/>


</View>


</View>


</View>


</Page>

);

const CoverInside = ({
  school = {},
}) => (

<Page
  size="A4"
  orientation="landscape"
  style={styles.page}
>

<View style={styles.spread}>


<View style={styles.panelLeft}>


<View style={styles.coverPanel}>


<View style={styles.goldLine}/>


<SchoolHeader
school={school}
/>



<Section
title="WELCOME MESSAGE"
variant="gold"
>


<Text style={styles.body}>

Dear Parents and Guardians,

{"\n\n"}

Welcome to{" "}

{
school.name ||
"Zion Praise Academy"
}.

{"\n\n"}

We appreciate the trust you have placed
in us to educate and develop your child.
Our commitment is to provide a learning
environment where students grow
academically, socially and morally.

{"\n\n"}

Through cooperation between parents,
teachers and students, we continue to
build a strong foundation for success,
leadership and responsible citizenship.

</Text>


</Section>





<Section
title="OUR MISSION"
>


<Bullet>
   Provide quality learning with the truth and strong phonics program; This is posible in three ways:
</Bullet>


<Bullet>
Capabilities to solve problem collaboratively and develop high level of Academic Mastery.
</Bullet>


<Bullet>
Connection to the world and each other.
</Bullet>


<Bullet>
Build leadership and responsibility.
</Bullet>


<Bullet>
Prepare learners for lifelong success. We want our students to establish meaningful networks that will help them
in the  future!
</Bullet>


</Section>





<Section
title="CORE VALUES"
variant="soft"
>


<View
style={{
flexDirection:"row",
flexWrap:"wrap"
}}
>


<View
style={{
width:"50%"
}}
>

<Bullet>
Excellence
</Bullet>


<Bullet>
Integrity
</Bullet>


<Bullet>
Discipline
</Bullet>


<Bullet>
Respect
</Bullet>


</View>




<View
style={{
width:"50%"
}}
>


<Bullet>
Leadership
</Bullet>


<Bullet>
Teamwork
</Bullet>


<Bullet>
Innovation
</Bullet>


<Bullet>
Service
</Bullet>


</View>


</View>


</Section>




<Section
title="EDUCATIONAL PHILOSOPHY"
variant="gold"
>


<Text style={styles.body}>

We believe every child has unique
potential. Our role is to discover,
develop and strengthen that potential
through quality teaching, mentorship
and a supportive school environment.

</Text>


</Section>



</View>


</View>






<View style={styles.panelRight}>


<View style={styles.coverPanel}>


<View style={styles.goldLine}/>




<Section
title="STUDENT CHARACTER DEVELOPMENT"
variant="soft"
>


<Bullet>
Academic Excellence
</Bullet>


<Bullet>
Moral and Ethical Values
</Bullet>


<Bullet>
Respect for Others
</Bullet>


<Bullet>
Critical Thinking Skills
</Bullet>


<Bullet>
Leadership Development
</Bullet>


<Bullet>
Effective Communication
</Bullet>


<Bullet>
Creativity and Innovation
</Bullet>


<Bullet>
Community Responsibility
</Bullet>


</Section>





<Section
title="SCHOOL DAILY SCHEDULE"
variant="gold"
>


<Bullet>
Monday - Friday
</Bullet>


<Bullet>
School Arrival: 7:30 AM
</Bullet>


<Bullet>
Morning Assembly: 7:45 AM
</Bullet>


<Bullet>
Classes Begin: 8:00 AM
</Bullet>


{/* <Bullet>
Break Time: 10:30 AM
</Bullet> */}


<Bullet>
Lunch Period: 12:30 PM
</Bullet>


<Bullet>
School Closing: 3:00 PM
</Bullet>


</Section>






<Section
title="PARENT RESPONSIBILITIES"
variant="danger"
>


<Bullet>
Ensure regular attendance.
</Bullet>


<Bullet>
Monitor assignments and homework.
</Bullet>


<Bullet>
Attend parent meetings.
</Bullet>


<Bullet>
Support school policies.
</Bullet>


<Bullet>
Maintain communication with teachers.
</Bullet>


</Section>






<Section
title="ADMISSION REQUIREMENTS"
>


<Bullet>
Completed admission application form.
</Bullet>


<Bullet>
Birth certificate.
</Bullet>


<Bullet>
Passport photographs.
</Bullet>


<Bullet>
Previous academic report.
</Bullet>


<Bullet>
Transfer documents where required.
</Bullet>


<Bullet>
Entrance assessment completion.
</Bullet>


</Section>



<Footer
school={school}
/>


</View>


</View>



</View>


</Page>

);



const ImportantInformation = ({
  school = {},
}) => (

<Page
size="A4"
orientation="landscape"
style={styles.page}
>


<View style={styles.spread}>


<View style={styles.panelLeft}>


<View style={styles.coverPanel}>


<View style={styles.goldLine}/>


<SchoolHeader
school={school}
/>




<Section
title="PARENT & SCHOOL PARTNERSHIP"
variant="gold"
>


<Text style={styles.body}>

The success of every learner depends
on strong cooperation between the
school and parents.

Parents are encouraged to participate
in school activities, monitor academic
progress and maintain communication
with teachers and administrators.

</Text>


</Section>





<Section
title="ATTENDANCE POLICY"
variant="soft"
>


<Bullet>
Students should arrive before 7:30 AM.
</Bullet>


<Bullet>
Parents should report absences.
</Bullet>


<Bullet>
Regular attendance improves performance.
</Bullet>


<Bullet>
Lateness must be avoided.
</Bullet>


<Bullet>
Students should participate fully in lessons.
</Bullet>


</Section>





<Section
title="STUDENT DISCIPLINE"
variant="danger"
>


<Bullet>
Respect teachers and classmates.
</Bullet>


<Bullet>
Protect school property.
</Bullet>


<Bullet>
Avoid bullying and misconduct.
</Bullet>


<Bullet>
Maintain proper uniform standards.
</Bullet>


<Bullet>
Demonstrate good character.
</Bullet>


</Section>





</View>


</View>





<View style={styles.panelRight}>


<View style={styles.coverPanel}>


<View style={styles.goldLine}/>



<Section
title="ACADEMIC EXPECTATIONS"
variant="gold"
>


<Bullet>
Complete assignments on time.
</Bullet>


<Bullet>
Participate actively in class.
</Bullet>


<Bullet>
Maintain good academic performance.
</Bullet>


<Bullet>
Prepare for examinations.
</Bullet>


<Bullet>
Develop daily reading habits.
</Bullet>


</Section>





<Section
title="GENERAL SCHOOL POLICIES"
variant="soft"
>


<Bullet>
Mobile devices require approval.
</Bullet>


<Bullet>
Visitors must report to administration.
</Bullet>


<Bullet>
Emergency information must remain updated.
</Bullet>


<Bullet>
Students must follow school regulations.
</Bullet>


</Section>





<Section
title="ADMINISTRATION MESSAGE"
variant="gold"
>


<Text style={styles.body}>

Thank you for choosing{" "}

{
school.name ||
"Zion Praise Academy"
}.

{"\n\n"}

We remain committed to developing
students who are academically strong,
morally responsible and prepared to
become future leaders.

</Text>


</Section>




<Section
title="CONTACT INFORMATION"
>


<Text style={styles.body}>

Address:{" "}
{
school.address ||
"Monrovia, Liberia"
}

{"\n"}

Phone:{" "}
{
school.phone ||
"+231 XXX XXX XXX"
}

{"\n"}

Email:{" "}
{
school.email ||
"info@zionpraiseacademy.com"
}

{"\n"}

Website:{" "}
{
school.website ||
"www.zionpraiseacademy.com"
}

</Text>


</Section>



<Footer
school={school}
/>



</View>


</View>



</View>


</Page>

);
const money = (value) =>
  `$${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;


const UniformSection = ({
  title,
  boys = [],
  girls = [],
}) => (

<Section
title={title}
variant="soft"
>

<View
style={{
flexDirection:"row",
}}
>


<View
style={{
width:"50%",
paddingRight:8,
}}
>

<Text style={styles.heading}>
BOYS
</Text>


{boys.map((item,index)=>(

<Bullet key={index}>
{item}
</Bullet>

))}


</View>



<View
style={{
width:"50%",
paddingLeft:8,
}}
>


<Text style={styles.heading}>
GIRLS
</Text>


{girls.map((item,index)=>(

<Bullet key={index}>
{item}
</Bullet>

))}


</View>


</View>


</Section>

);




const FeeTable = ({
 title,
 data = [],
}) => (

<Section
title={title}
variant="gold"
>


<View
style={{
flexDirection:"row",
backgroundColor:COLORS.BLACK,
paddingVertical:5,
borderRadius:3,
}}
>


{
[
"CLASS",
"TYPE",
"REG",
"1ST",
"2ND",
"3RD",
"YEAR"
].map((head,index)=>(


<Text
key={index}
style={{
width:"14.28%",
fontSize:7,
color:COLORS.WHITE,
fontWeight:"bold",
textAlign:"center",
}}
>

{head}

</Text>


))
}


</View>



{
data.length === 0 ? (

<Text
style={{
textAlign:"center",
fontSize:8,
color:COLORS.MUTED,
marginTop:10,
}}
>

No fee information available.

</Text>

) : (

data.map((item,index)=>(


<View
key={index}
style={{
flexDirection:"row",
paddingVertical:4,
borderBottomWidth:1,
borderBottomColor:COLORS.BORDER,
backgroundColor:
index % 2 === 0
?
COLORS.LIGHT
:
COLORS.WHITE,
}}
>


<Text
style={{
width:"14.28%",
fontSize:7,
textAlign:"center",
}}
>

{
item.grade_class_name
}

</Text>



<Text
style={{
width:"14.28%",
fontSize:7,
textAlign:"center",
}}
>

{
item.student_type
}

</Text>



<Text
style={{
width:"14.28%",
fontSize:7,
textAlign:"center",
}}
>

{
money(item.registration)
}

</Text>



<Text
style={{
width:"14.28%",
fontSize:7,
textAlign:"center",
}}
>

{
money(item.first_installment)
}

</Text>



<Text
style={{
width:"14.28%",
fontSize:7,
textAlign:"center",
}}
>

{
money(item.second_installment)
}

</Text>



<Text
style={{
width:"14.28%",
fontSize:7,
textAlign:"center",
}}
>

{
money(item.third_installment)
}

</Text>



<Text
style={{
width:"14.28%",
fontSize:7,
fontWeight:"bold",
textAlign:"center",
}}
>

{
money(item.yearly_school_fee)
}

</Text>



</View>


))

)


}


</Section>

);





const FeesAndUniformPage = ({
fees=[],
school={}
}) => {


const safeFees =
Array.isArray(fees)
?
fees
:
[];

const oldStudents =
safeFees.filter(
item =>
item.student_type === "old"
);


const newStudents =
safeFees.filter(
item =>
item.student_type === "new"
);


const scholarship =
safeFees.filter(
item =>
item.student_type === "scholarship"
);



return (

<Page
size="A4"
orientation="landscape"
style={styles.page}
>


<View style={styles.spread}>


<View style={styles.panelLeft}>


<View style={styles.coverPanel}>


<View style={styles.goldLine}/>



<Section
title="SCHOOL UNIFORM POLICY"
variant="gold"
>


<Text style={styles.body}>

Students are required to wear the
approved school uniform neatly and
properly every school day.

Uniforms must always be clean,
complete and maintained according
to school standards.

</Text>


</Section>





<UniformSection

title="NURSERY & KINDERGARTEN"

boys={[
"School Shirt",
"School Shorts",
"White Socks",
"Black Shoes"
]}


girls={[
"School Blouse",
"School Skirt",
"White Socks",
"Black Shoes"
]}

/>





<UniformSection

title="PRIMARY & JUNIOR HIGH"

boys={[
"School Shirt",
"Black Trousers",
"School Tie",
"Black Shoes"
]}


girls={[
"School Blouse",
"School Skirt",
"School Tie",
"Black Shoes"
]}

/>





<Section
title="UNIFORM RULES"
variant="danger"
>


<Bullet>
No unauthorized hairstyles.
</Bullet>


<Bullet>
No slippers during school hours.
</Bullet>


<Bullet>
Students must maintain hygiene.
</Bullet>


<Bullet>
School ID must be worn daily.
</Bullet>


</Section>



</View>


</View>






<View style={styles.panelRight}>


<View style={styles.coverPanel}>


<View style={styles.goldLine}/>



<SchoolHeader
school={school}
/>



<FeeTable
title="OLD STUDENTS"
data={oldStudents}
/>


<FeeTable
title="NEW STUDENTS"
data={newStudents}
/>


<FeeTable
title="SCHOLARSHIP STUDENTS"
data={scholarship}
/>



<Footer
school={school}
/>


</View>


</View>



</View>



</Page>

);


};
const StudentHandbookPage = ({
  school = {},
}) => (

<Page
  size="A4"
  orientation="landscape"
  style={styles.page}
>

<View style={styles.spread}>


<View style={styles.panelLeft}>


<View style={styles.coverPanel}>


<View style={styles.goldLine}/>



<Section
title="STUDENT LIFE & EXPECTATIONS"
variant="gold"
>


<Text style={styles.body}>

Every student is expected to represent
the school positively through good
character, discipline and commitment
to learning.

The school encourages learners to
develop confidence, creativity and
responsibility both inside and outside
the classroom.

</Text>


</Section>





<Section
title="CLASSROOM EXPECTATIONS"
variant="soft"
>


<Bullet>
Arrive prepared with required materials.
</Bullet>


<Bullet>
Respect teachers and classmates.
</Bullet>


<Bullet>
Participate actively in lessons.
</Bullet>


<Bullet>
Complete assignments on time.
</Bullet>


<Bullet>
Maintain a positive learning attitude.
</Bullet>


</Section>





<Section
title="HEALTH & SAFETY"
variant="danger"
>


<Bullet>
Maintain personal cleanliness.
</Bullet>


<Bullet>
Report injuries or illness immediately.
</Bullet>


<Bullet>
Follow all safety instructions.
</Bullet>


<Bullet>
Avoid harmful behaviour.
</Bullet>


<Bullet>
Protect personal and school property.
</Bullet>


</Section>




<Section
title="EXTRACURRICULAR ACTIVITIES"
>


<Bullet>
Sports and physical development.
</Bullet>


<Bullet>
Academic clubs.
</Bullet>


<Bullet>
Leadership programs.
</Bullet>


<Bullet>
Cultural activities.
</Bullet>


<Bullet>
Community service projects.
</Bullet>


</Section>



</View>


</View>







<View style={styles.panelRight}>


<View style={styles.coverPanel}>


<View style={styles.goldLine}/>



<Section
title="WAEC & EXAMINATION PREPARATION"
variant="gold"
>


<Text style={styles.body}>

Senior students preparing for external
examinations are expected to maintain
excellent attendance, complete required
assessments and follow all examination
regulations.

The school provides academic support
through revision programs, continuous
assessment and teacher guidance.

</Text>


</Section>





<Section
title="GRADUATION REQUIREMENTS"
variant="soft"
>


<Bullet>
Successful completion of required subjects.
</Bullet>


<Bullet>
Completion of internal assessments.
</Bullet>


<Bullet>
Successful external examination results.
</Bullet>


<Bullet>
Good disciplinary record.
</Bullet>


<Bullet>
Completion of school clearance process.
</Bullet>


</Section>





<Section
title="ACADEMIC SUPPORT SERVICES"
>


<Bullet>
Teacher consultation sessions.
</Bullet>


<Bullet>
Extra learning assistance.
</Bullet>


<Bullet>
Student counselling.
</Bullet>


<Bullet>
Career guidance.
</Bullet>


<Bullet>
Parent academic updates.
</Bullet>


</Section>





<Section
title="FINAL MESSAGE"
variant="gold"
>


<Text style={styles.body}>

Thank you for partnering with{" "}

{
school.name ||
"Zion Praise Academy"
}

.

Together, we create an environment
where every student can discover
their abilities, develop strong values
and achieve their goals.

</Text>


</Section>



<Footer
school={school}
/>


</View>


</View>



</View>


</Page>

);





const InformationSheetPDF = ({
  fees = [],
  academicYear = {},
  school = {},
}) => (

<Document
title={
`${school.name || "School"} Information Handbook`
}

author={
school.name ||
"School Management"
}

subject="School Information and Fee Handbook"

creator="React PDF Renderer"

>


<CoverOutside

academicYear={academicYear}

school={school}

/>



<CoverInside

school={school}

/>



<FeesAndUniformPage

fees={fees}

school={school}

/>



<StudentHandbookPage

school={school}

/>



</Document>

);



export default InformationSheetPDF;